/**
 * Admin Announcements Management Page
 * 
 * Features:
 * - Create/Edit/Delete announcements
 * - Toggle active status
 * - AI-powered email generation for all types
 * - Email preview before sending
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminRole } from "@/hooks/useAdminRole";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft,
  Plus,
  Calendar,
  Megaphone,
  Lightbulb,
  Sparkles,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  Mail,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import {
  useAllAnnouncements,
  useCreateAnnouncement,
  useUpdateAnnouncement,
  useDeleteAnnouncement,
  type Announcement,
  type AnnouncementType,
} from "@/hooks/useAnnouncements";
import { EmailPreviewDialog } from "@/components/admin/EmailPreviewDialog";

const TYPE_CONFIG: Record<AnnouncementType, { label: string; icon: React.ReactNode; color: string }> = {
  event: { label: "Event", icon: <Calendar className="w-4 h-4" />, color: "bg-violet-100 text-violet-600" },
  banner: { label: "Banner", icon: <Megaphone className="w-4 h-4" />, color: "bg-blue-100 text-blue-600" },
  tip: { label: "Tip", icon: <Lightbulb className="w-4 h-4" />, color: "bg-amber-100 text-amber-600" },
  update: { label: "Update", icon: <Sparkles className="w-4 h-4" />, color: "bg-emerald-100 text-emerald-600" },
};

interface AnnouncementFormData {
  title: string;
  content: string;
  type: AnnouncementType;
  cta_text: string;
  cta_url: string;
  event_date: string;
  starts_at: string;
  ends_at: string;
  is_active: boolean;
}

const defaultFormData: AnnouncementFormData = {
  title: "",
  content: "",
  type: "banner",
  cta_text: "",
  cta_url: "",
  event_date: "",
  starts_at: new Date().toISOString().slice(0, 16),
  ends_at: "",
  is_active: true,
};

const AnnouncementsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  useAuth();
  const { isAdmin, isLoading: isAdminLoading } = useAdminRole();

  const { data: announcements, isLoading } = useAllAnnouncements();
  const createMutation = useCreateAnnouncement();
  const updateMutation = useUpdateAnnouncement();
  const deleteMutation = useDeleteAnnouncement();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [formData, setFormData] = useState<AnnouncementFormData>(defaultFormData);
  
  // Email preview state
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [emailPreviewAnnouncement, setEmailPreviewAnnouncement] = useState<{
    id?: string;
    title: string;
    content: string;
    type: AnnouncementType;
    event_date?: string;
    cta_text?: string;
    cta_url?: string;
  } | null>(null);

  // Redirect non-admins
  if (!isAdminLoading && !isAdmin) {
    navigate("/admin", { replace: true });
    return null;
  }

  const handleOpenCreate = () => {
    setEditingAnnouncement(null);
    setFormData(defaultFormData);
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content || "",
      type: announcement.type,
      cta_text: announcement.cta_text || "",
      cta_url: announcement.cta_url || "",
      event_date: announcement.event_date ? announcement.event_date.slice(0, 16) : "",
      starts_at: announcement.starts_at.slice(0, 16),
      ends_at: announcement.ends_at ? announcement.ends_at.slice(0, 16) : "",
      is_active: announcement.is_active,
    });
    setIsDialogOpen(true);
  };

  const handleOpenEmailPreview = (announcement: Announcement) => {
    setEmailPreviewAnnouncement({
      id: announcement.id,
      title: announcement.title,
      content: announcement.content || "",
      type: announcement.type,
      event_date: announcement.event_date || undefined,
      cta_text: announcement.cta_text || undefined,
      cta_url: announcement.cta_url || undefined,
    });
    setEmailPreviewOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const announcementData = {
      title: formData.title,
      content: formData.content || null,
      type: formData.type,
      cta_text: formData.cta_text || null,
      cta_url: formData.cta_url || null,
      event_date: formData.event_date ? new Date(formData.event_date).toISOString() : null,
      starts_at: new Date(formData.starts_at).toISOString(),
      ends_at: formData.ends_at ? new Date(formData.ends_at).toISOString() : null,
      is_active: formData.is_active,
      target_audience: ["all"],
    };

    try {
      if (editingAnnouncement) {
        await updateMutation.mutateAsync({
          id: editingAnnouncement.id,
          updates: announcementData,
        });
        toast({ title: "Announcement updated!" });
      } else {
        await createMutation.mutateAsync(announcementData);
        toast({ title: "Announcement created!" });
      }

      setIsDialogOpen(false);
      setFormData(defaultFormData);
    } catch (error) {
      console.error("Failed to save announcement:", error);
      toast({
        title: "Failed to save",
        description: "Check console for details",
        variant: "destructive",
      });
    }
  };

  const handleToggleActive = async (announcement: Announcement) => {
    await updateMutation.mutateAsync({
      id: announcement.id,
      updates: { is_active: !announcement.is_active },
    });
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this announcement?")) return;
    await deleteMutation.mutateAsync(id);
    toast({ title: "Announcement deleted" });
  };

  if (isAdminLoading || isLoading) {
    return (
      <AppLayout pageTitle="Announcements">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <Skeleton className="h-10 w-64 mb-8" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout pageTitle="Announcements">
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/admin")}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Announcements</h1>
                <p className="text-muted-foreground text-sm">
                  Manage in-app announcements and email notifications
                </p>
              </div>
            </div>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={handleOpenCreate}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Announcement
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingAnnouncement ? "Edit Announcement" : "Create Announcement"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  {/* Type */}
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select
                      value={formData.type}
                      onValueChange={(v) => setFormData({ ...formData, type: v as AnnouncementType })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(TYPE_CONFIG).map(([key, config]) => (
                          <SelectItem key={key} value={key}>
                            <div className="flex items-center gap-2">
                              {config.icon}
                              {config.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Title */}
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Join Our First Office Hours!"
                      required
                    />
                  </div>

                  {/* Content */}
                  <div className="space-y-2">
                    <Label>Content</Label>
                    <Textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder="Additional details..."
                      rows={3}
                    />
                  </div>

                  {/* Event Date (only for events) */}
                  {formData.type === "event" && (
                    <div className="space-y-2">
                      <Label>Event Date & Time</Label>
                      <Input
                        type="datetime-local"
                        value={formData.event_date}
                        onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                      />
                    </div>
                  )}

                  {/* CTA */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Button Text</Label>
                      <Input
                        value={formData.cta_text}
                        onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                        placeholder="Join Now"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Button URL</Label>
                      <Input
                        value={formData.cta_url}
                        onChange={(e) => setFormData({ ...formData, cta_url: e.target.value })}
                        placeholder="https://zoom.us/..."
                      />
                    </div>
                  </div>

                  {/* Display Period */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Show From</Label>
                      <Input
                        type="datetime-local"
                        value={formData.starts_at}
                        onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Show Until (optional)</Label>
                      <Input
                        type="datetime-local"
                        value={formData.ends_at}
                        onChange={(e) => setFormData({ ...formData, ends_at: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Active Toggle */}
                  <div className="flex items-center justify-between py-2">
                    <Label>Active</Label>
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    />
                  </div>

                  {/* Submit */}
                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={createMutation.isPending || updateMutation.isPending}
                    >
                      {editingAnnouncement ? "Save Changes" : "Create"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Announcements List */}
          <div className="space-y-4">
            {!announcements?.length ? (
              <div className="text-center py-12 text-muted-foreground">
                No announcements yet. Create your first one!
              </div>
            ) : (
              announcements.map((announcement) => {
                const typeConfig = TYPE_CONFIG[announcement.type];
                return (
                  <div
                    key={announcement.id}
                    className={`bg-card border rounded-xl p-4 transition-all ${
                      announcement.is_active ? "border-border" : "border-border/50 opacity-60"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={`p-2 rounded-lg ${typeConfig.color}`}>
                        {typeConfig.icon}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground truncate">
                            {announcement.title}
                          </h3>
                          {!announcement.is_active && (
                            <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">
                              Inactive
                            </span>
                          )}
                        </div>
                        {announcement.content && (
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {announcement.content}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span>{typeConfig.label}</span>
                          <span>•</span>
                          <span>
                            {format(new Date(announcement.created_at), "MMM d, yyyy")}
                          </span>
                          {announcement.event_date && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {format(new Date(announcement.event_date), "MMM d, h:mm a")}
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        {/* Email Button - for all types */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenEmailPreview(announcement)}
                          title="Send email notification"
                          className="text-primary hover:text-primary"
                        >
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleActive(announcement)}
                          title={announcement.is_active ? "Deactivate" : "Activate"}
                        >
                          {announcement.is_active ? (
                            <Eye className="w-4 h-4" />
                          ) : (
                            <EyeOff className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenEdit(announcement)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(announcement.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Email Preview Dialog */}
      {emailPreviewAnnouncement && (
        <EmailPreviewDialog
          open={emailPreviewOpen}
          onOpenChange={setEmailPreviewOpen}
          announcement={emailPreviewAnnouncement}
          onEmailSent={() => {
            toast({ title: "Email sending started!" });
          }}
        />
      )}
    </AppLayout>
  );
};

export default AnnouncementsPage;
